/* eslint-disable @typescript-eslint/no-unused-vars */
import { PdpAIModal } from 'common/ui/shared/pdp-ai-modal';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ConditionalInstructionsModal } from 'common/ui/shared/conditional-instructions-modal';
import { useSelectedContentContext } from 'common/ui/shared/selected-content-ctx';
import { userService } from 'common/services/user.service';
import { RetailerType } from 'common/constants/entities';
import { useGeneratePDP } from 'common/ui/shared/pdp-ai-modal/use-generate-pdp';
import { useAIGenerationsStatusMutation } from 'common/hooks/api/mutations/use-products-mutation';
import { getCategoryIDKeyFromProduct } from 'modules/common/content/utils/mappings';
import { KeywordsSettingsModal } from '../keywords-settings-modal';
import { useSelectedProductQuery } from '../../hooks/queries';
import { PDPGenerationStatus } from '../../constants';

const modalsCtx = createContext({
  pdpAttributesWithIssuesOnly: false,
  pdpGeneration: false,
  pdpSeoOptimization: false,
  pdpGeoOptimization: false,
  enhanceWithSeasonality: false,
  seasonalitySourceCopy: 'seo_optimized_pdp' as string,
  geoRefetchReviews: false,
  pdpLoadingStatus: PDPGenerationStatus.IDLE,
  isGenerating: false,
  handleGenerateAI: (): void => undefined,
  triggerAIModal: (): void => undefined,
  triggerInstructionsModal: (): void => undefined,
  triggerKWsModal: (): void => undefined,
  triggerGenerationStatus: (value?: PDPGenerationStatus): void => undefined,
  setPdpAttributesWithIssuesOnly: (value: boolean): void => undefined,
  setPdpGeneration: (value: boolean): void => undefined,
  setPdpSeoOptimization: (value: boolean): void => undefined,
  setPdpGeoOptimization: (value: boolean): void => undefined,
  setEnhanceWithSeasonality: (value: boolean): void => undefined,
  setSeasonalitySourceCopy: (value: string): void => undefined,
  setGeoRefetchReviews: (value: boolean): void => undefined,
});

export function ModalsContext({ children }: PropsWithChildren) {
  const defaultPdpSeoOptimization = !userService.ensureRetailer(RetailerType.Autozone);

  const { selectedContent } = useSelectedContentContext();

  const { data: productData } = useSelectedProductQuery();

  const [kwsModalOpen, setKWsModalOpen] = useState<boolean>(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState<boolean>(false);
  const [instructionsModalOpen, setInstructionsModalOpen] = useState<boolean>(false);
  const [pdpAttributesWithIssuesOnly, setPdpAttributesWithIssuesOnly] = useState<boolean>(false);
  const [pdpGeneration, setPdpGeneration] = useState<boolean>(false);
  const [pdpSeoOptimization, setPdpSeoOptimization] = useState<boolean>(defaultPdpSeoOptimization);
  const [pdpGeoOptimization, setPdpGeoOptimization] = useState<boolean>(true);
  const [enhanceWithSeasonality, setEnhanceWithSeasonality] = useState<boolean>(false);
  const [seasonalitySourceCopy, setSeasonalitySourceCopy] = useState<string>('optimized_pdp');
  const [geoRefetchReviews, setGeoRefetchReviews] = useState<boolean>(false);
  const [pdpLoadingStatus, setPdpLoadingStatus] = useState<PDPGenerationStatus>(PDPGenerationStatus.IDLE);

  const { mutate: mutateAIGenerationsStatus } = useAIGenerationsStatusMutation({
    productID: selectedContent,
  });

  const triggerAIModal = useCallback(() => {
    setIsAIModalOpen(true);
  }, []);

  const onAIModalClose = () => {
    setIsAIModalOpen(false);
  };

  const triggerInstructionsModal = useCallback(() => {
    setInstructionsModalOpen(true);
  }, []);

  const onInstructionsModalClose = () => {
    setInstructionsModalOpen(false);
  };

  const triggerKWsModal = useCallback(() => {
    setKWsModalOpen(true);
  }, []);

  const triggerGenerationStatus = useCallback(
    (status?: PDPGenerationStatus) => {
      if (!status || status !== PDPGenerationStatus.IDLE) {
        setPdpLoadingStatus(prev =>
          prev === PDPGenerationStatus.IDLE ? PDPGenerationStatus.PDP_OPTIMIZATION_RUNNING : prev
        );
        setTimeout(() => {
          mutateAIGenerationsStatus(null, {
            onSuccess: (s: PDPGenerationStatus) => {
              if (s !== PDPGenerationStatus.IDLE) {
                triggerGenerationStatus(s);
              }
              setPdpLoadingStatus(s);
            },
          });
        }, 1000);
      } else {
        mutateAIGenerationsStatus();
      }
    },
    [mutateAIGenerationsStatus]
  );

  const onKWsModalClose = () => {
    setKWsModalOpen(false);
  };

  const { handleGenerateAI, isLoading: isGenerating } = useGeneratePDP({
    issuesOnly: pdpAttributesWithIssuesOnly,
    pdpGen: pdpGeneration,
    seoGen: pdpSeoOptimization,
    geoGen: pdpGeoOptimization,
    geoRefetchReviews,
    enhanceWithSeasonality,
    seasonalitySourceCopy,
    pid: selectedContent,
    onLoadingGenerateStatus: triggerGenerationStatus,
  });

  useEffect(() => {
    triggerGenerationStatus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => {
    return {
      pdpAttributesWithIssuesOnly,
      pdpGeneration,
      pdpSeoOptimization,
      pdpGeoOptimization,
      enhanceWithSeasonality,
      seasonalitySourceCopy,
      geoRefetchReviews,
      pdpLoadingStatus,
      isGenerating,
      handleGenerateAI,
      triggerAIModal,
      triggerInstructionsModal,
      triggerKWsModal,
      triggerGenerationStatus,
      setPdpAttributesWithIssuesOnly,
      setPdpGeneration,
      setPdpSeoOptimization,
      setPdpGeoOptimization,
      setEnhanceWithSeasonality,
      setSeasonalitySourceCopy,
      setGeoRefetchReviews,
    };
  }, [
    pdpAttributesWithIssuesOnly,
    pdpGeneration,
    pdpSeoOptimization,
    pdpGeoOptimization,
    enhanceWithSeasonality,
    seasonalitySourceCopy,
    geoRefetchReviews,
    pdpLoadingStatus,
    isGenerating,
    handleGenerateAI,
    triggerAIModal,
    triggerInstructionsModal,
    triggerKWsModal,
    triggerGenerationStatus,
    setPdpAttributesWithIssuesOnly,
    setPdpGeneration,
    setPdpSeoOptimization,
    setPdpGeoOptimization,
    setEnhanceWithSeasonality,
    setSeasonalitySourceCopy,
  ]);

  return (
    <modalsCtx.Provider value={value}>
      <PdpAIModal pid={selectedContent} open={isAIModalOpen} onClose={onAIModalClose} />
      <KeywordsSettingsModal
        open={kwsModalOpen}
        onClose={onKWsModalClose}
        pid={selectedContent}
        kwTimeRange={productData?.seo_keywords_time_range}
      />
      <ConditionalInstructionsModal
        open={instructionsModalOpen}
        onClose={onInstructionsModalClose}
        pid={selectedContent}
        categoryID={(productData ?? {})[getCategoryIDKeyFromProduct()]}
      />
      {children}
    </modalsCtx.Provider>
  );
}

export function useModalsContext() {
  const ctx = useContext(modalsCtx);
  return ctx;
}
